import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { forLifeOf, LifecycleAware, LsqValidators } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginService } from '../login.service';

@LifecycleAware()
@Component({
    selector: 'forced-reset-password',
    styleUrls: ['./login-step.scss'],
    template: `
        <h1 i18n>Reset Password</h1>

        <form class="login-step" [formGroup]="formGroup" (ngSubmit)="handleSubmit()">
            <p class="error" *ngIf="showLoginError" i18n>
                Error: Invalid email address/password. Please try again.
            </p>

            <p class="body" [style.textAlign]="'center'" [style.marginBottom]="'2rem'" i18n>
                A new password is required.
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Email Address</p>
            <lsq-input
                [style.marginBottom]="'2rem'"
                name="emailAddress"
                type="email"
                [basicLabel]="false"
                formControlName="emailAddress"
                [group]="formGroup"
            >
            </lsq-input>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'0.5rem'" i18n>Temporary Password</p>
            <p class="body" [style.marginBottom]="'1rem'" i18n>
                Please use the temporary password that was sent to your email.
            </p>
            <lsq-input
                [style.marginBottom]="'3rem'"
                type="password"
                [basicLabel]="false"
                name="oldPassword"
                formControlName="oldPassword"
                [group]="formGroup"
            >
            </lsq-input>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'0.5rem'" i18n>New Password</p>

            <p class="body" [style.marginBottom]="'2rem'" i18n>
                Passwords must be at least 8 characters, contain one number, one capital letter, one
                lowercase letter, and one special character.
            </p>

            <lsq-input
                [style.marginBottom]="'2rem'"
                type="password"
                [basicLabel]="false"
                name="newPassword"
                formControlName="newPassword"
                [group]="formGroup"
            >
            </lsq-input>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Confirm Password</p>
            <lsq-input
                [style.marginBottom]="'3rem'"
                type="password"
                [basicLabel]="false"
                name="confirmPassword"
                formControlName="confirmPassword"
                [group]="formGroup"
            >
            </lsq-input>

            <lsq-button
                type="submit"
                size="block"
                [submitting]="isSubmitting"
                [style.marginBottom]="'2rem'"
                i18n
            >
                Reset my password
            </lsq-button>
        </form>
    `
})
export class ForcedResetPasswordComponent implements OnDestroy {
    public isSubmitting = false;
    public showLoginError = false;

    private readonly _passwordsMustMatchCopy = this._i18n({
        id: 'resetPasswordAndConfirmMustMatch',
        value: `"New Password" and "Confirm Password" must match.`
    });
    public readonly resetPasswordOldPassMatchesError = this._i18n({
        id: 'newPasswordMustNotMatchOldPassword',
        value: 'You cannot use the same password. Please enter a different password.'
    });

    public formGroup = this._formBuilder.group({
        emailAddress: [
            { value: this._loginService.cachedEmailAddress, disabled: true },
            [Validators.required, LsqValidators.email]
        ],
        oldPassword: ['', [Validators.required]],
        newPassword: [
            '',
            [
                Validators.required,
                LsqValidators.strictPassword,
                LsqValidators.notMatchValue(
                    () => this.formGroup?.get('oldPassword')?.value ?? '',
                    this.resetPasswordOldPassMatchesError
                ),
                LsqValidators.matchValueSource(
                    () => this.formGroup?.get('confirmPassword'),
                    this._passwordsMustMatchCopy
                )
            ]
        ],
        confirmPassword: [
            '',
            [
                Validators.required,
                LsqValidators.matchValueConfirm(
                    () => this.formGroup?.get('newPassword'),
                    this._passwordsMustMatchCopy
                )
            ]
        ]
    });

    constructor(
        private _i18n: I18n,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService
    ) {
        this._loginService.routeParams$.pipe(forLifeOf(this)).subscribe((params) => {
            if (params.get('username')) {
                this.formGroup.patchValue({ emailAddress: params.get('username') });
                this.formGroup.get('emailAddress').disable();
            }
        });
    }

    public ngOnDestroy(): void {}

    public async handleSubmit(): Promise<void> {
        this.formGroup.markAllAsTouched();

        if (this.formGroup.valid) {
            this.isSubmitting = true;
            this.showLoginError = false;

            try {
                await this._loginService.completeForcedPasswordReset(
                    this.formGroup.get('emailAddress').value,
                    this.formGroup.get('oldPassword').value,
                    this.formGroup.get('newPassword').value
                );
            } catch {
                this.showLoginError = true;
            }
        }
        this.isSubmitting = false;
    }
}
